import { useId, useState } from 'react';
import { useT } from '../../i18n';
import { isNativeShell } from '../../lib/nativeBridge';
import type { AccountSignInSession } from '../../lib/api/accountSignIn';
import { Padel24Logo } from '../ui';
import { AccountCodeSignIn } from './AccountCodeSignIn';
import { PasskeySignInAction, type PasskeySignInFailure } from './PasskeySignInAction';

// ─────────────────────────────────────────────────────────────────────
// SIGNING IN AS A PLAYER, WITHOUT A CLUB (#710).
//
// *«A new or occasional player should be able to … identify themselves with an
// e-mail code, and book or participate with as little friction as possible.
// Club membership is optional.»*
//
// ── IT IS *THE* DOOR NOW, NOT ONE OF TWO ─────────────────────────
//
// It shipped as a second door beside the club-member screen, with a «log in as
// a player» button to choose between them. The direction correction of
// 2026-10-13 removed that: *«A user opening the player app should never be
// asked which kind of user they are»* — and inside the player app «as a
// player» said nothing anyway.
//
// So there is no club field, and no way back to another door, because there is
// no other door. `MemberSignInScreen`, which drove the legacy
// `/auth/member/otp`, is retired with that door (#750).
//
// ── IT KNOWS NO MORE THAN THE SERVER SAYS ─────────────────────────
//
// `request` answers the same 202 for an address that is unknown, known, or
// belongs to a disabled account. So this moves to the code step either way and
// the copy promises only that a code was sent — saying «we found you» would be
// an address oracle built on the client after the server refused to be one.
//
// ── THE FLOW ITSELF IS SHARED NOW (#837 review) ───────────────────
//
// The two steps live in `AccountCodeSignIn`, because the club portal's
// entrance is the same two steps against the same `/auth/otp`. This file is
// the PLAYER's chrome around them; `LoginScreen` is the portal's. One
// implementation, one endpoint, one session.
//
// ── THE SESSION IS HANDED UP, NOT INSTALLED ───────────────────────
//
// `onSignedIn` receives it. Which holder owns an account session, and which
// surface the app renders afterwards, is the gate's decision — one place owns
// what «signed in» means, and a screen that also set it would be a second.
//
// ── A PASSKEY, AS THE SECONDARY DOOR (#676 follow-up) ─────────────
//
// The member app's Android WebView can run a WebAuthn ceremony (#676) and the
// server accepts the app's origin (#968), so the player entrance offers the
// same passkey door the club portal does — the SAME component,
// `PasskeySignInAction`, not a copy. `docs/auth/PASSWORDLESS.md`: the e-mail
// code is primary, a passkey optional and secondary, so it sits behind «Andre
// måter å logge inn på» under the e-mail field, exactly as on the portal.
//
// Optional, and absent means NOT RENDERED: the gate withholds it where the
// backend has no passkey door or the browser has no WebAuthn. An offer that
// cannot be honoured is worse than no offer.
//
// ── «HUSK MEG PÅ DENNE ENHETEN» (#1091) ───────────────────────────
//
// The device policy decided 2026-10-11 (#750): in a BROWSER the session is
// twelve hours unless the person ticks this, which asks for thirty days; in
// the NATIVE APP it is always thirty days and there is no box, because an app
// on a phone is the remembered device the policy names. Unticked by default —
// the safe value is what happens if they do nothing — and it applies to both
// doors on this card, the code and the passkey. It is offered HERE and not in
// `AccountCodeSignIn`, which the club portal shares: the portal keeps twelve
// hours. A long session is identity only; admin authority still needs a
// recent sign-in (`docs/auth/SESSION_FRESHNESS.md`).
// ─────────────────────────────────────────────────────────────────────

export interface PlayerSignInScreenProps {
  /** Called with the session the door issued, once a code is accepted. */
  onSignedIn: (session: AccountSignInSession, email: string) => void;
  /**
   * Run a passkey sign-in. Resolves to `null` on success — the gate adopts the
   * session and this screen goes away — or to a FAILURE CODE, which the shared
   * action turns into a sentence. Absent: no passkey offer is rendered.
   */
  onSignInWithPasskey?: (remember: boolean) => Promise<PasskeySignInFailure | null>;
}

export function PlayerSignInScreen({ onSignedIn, onSignInWithPasskey }: PlayerSignInScreenProps) {
  const t = useT();
  const hintId = useId();
  // Read once: the shell does not change under a running page.
  const [native] = useState(() => isNativeShell());
  const [ticked, setTicked] = useState(false);
  const remember = native || ticked;
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#6b21a8' }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {/* THE WHITE WORDMARK, as #818 chose for the club screen on the
                same purple. This screen kept the dark asset — legible, but
                the dark one is drawn for `--paper` and this ground is
                `#6b21a8`. Found by looking at it (#751 browser pass). */}
            <Padel24Logo size="xl" variant="full" tone="light" />
          </div>
          {/* ON THE PURPLE, NOT ON A CARD (#751 browser pass). `text-ink-3` is
              a grey chosen for `--paper`; on this `#6b21a8` it measures
              2.78:1, well under the 4.5:1 floor for text this size (#781).
              80% white is 6.11:1. */}
          <p className="text-sm text-white/80">{t('memberSignIn.tagline')}</p>
        </div>

        <div className="card p-6">
          {/* The portal's column, so the disclosure sits under the e-mail
              field with the same spacing. Without a passkey offer the column
              holds the code flow alone, as before. */}
          <div className="space-y-3">
            <AccountCodeSignIn onSignedIn={onSignedIn} remember={remember} />
            {!native && (
              <div className="flex items-start gap-2 pt-1">
                <input
                  id={`${hintId}-remember`}
                  type="checkbox"
                  checked={ticked}
                  onChange={(e) => setTicked(e.target.checked)}
                  aria-describedby={hintId}
                  className="mt-0.5 h-4 w-4 accent-lime-deep"
                />
                <div>
                  <label htmlFor={`${hintId}-remember`} className="text-sm text-ink">
                    {t('memberSignIn.remember')}
                  </label>
                  <p id={hintId} className="text-xs text-ink-3">
                    {t('memberSignIn.rememberHint')}
                  </p>
                </div>
              </div>
            )}
            {onSignInWithPasskey && (
              <PasskeySignInAction onSignInWithPasskey={() => onSignInWithPasskey(remember)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
